import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentDropdown } from "@/components/agent-dropdown";
import { ProjectShareSection, type ProjectShareSettings } from "@/components/project-share-section";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { Agent } from "@/hooks/use-agents";
import { useAutoResize } from "@/hooks/use-auto-resize";
import type { Project, ProjectUpdateRequest } from "@/lib/project-types";

interface ProjectEditDialogProps {
  open: boolean;
  project: Project | null;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  userRole?: string;
  getToken: () => Promise<string>;
  onOpenChange: (open: boolean) => void;
  onSave: (projectId: string, input: ProjectUpdateRequest) => Promise<boolean>;
}

/** Edit the project fields that determine how new project work is handled. */
export function ProjectEditDialog({
  open,
  project,
  agents,
  agentsLoading,
  agentsError,
  userRole,
  getToken,
  onOpenChange,
  onSave,
}: ProjectEditDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [directive, setDirective] = useState("");
  const [defaultAgentId, setDefaultAgentId] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const {
    ref: directiveResizeRef,
    resize: resizeDirective,
    resizeForValueChange: resizeDirectiveForValueChange,
  } = useAutoResize(10, 1);

  useEffect(() => {
    if (!open || !project) return;
    setName(project.name);
    setDirective(project.directive);
    setDefaultAgentId(project.default_agent_id);
    setIsPrivate(project.is_private);
    setAllowedRoles(project.allowed_roles);
    setSaving(false);
  }, [open, project?.id]);

  useEffect(() => {
    if (!open || !project) return;
    const frame = requestAnimationFrame(resizeDirective);
    return () => cancelAnimationFrame(frame);
  }, [open, project?.id, resizeDirective]);

  const canSave = Boolean(project && name.trim() && defaultAgentId && !agentsLoading);
  // Wiki links are server-discovered metadata. Split their existing roles only
  // for display; keep them out of editable state and the update payload so this
  // dialog stays compatible with older servers.
  const wikiLinks = project?.wiki_links ?? [];
  const primaryWikiLinks = wikiLinks.filter((link) => link.role === "primary");
  const referenceWikiLinks = wikiLinks.filter((link) => link.role === "reference");

  const handleSave = async () => {
    if (!project || !canSave || saving) return;

    setSaving(true);
    try {
      const saved = await onSave(project.id, {
        name: name.trim(),
        directive: directive.trim(),
        default_agent_id: defaultAgentId,
        is_private: isPrivate,
        allowed_roles: allowedRoles,
      });
      if (saved) onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultAgentChange = (agentId: string) => {
    setDefaultAgentId(agentId);
    // Changing the default agent should seed an otherwise open project with
    // that agent's invoke roles. Preserve an explicit private or role-scoped
    // choice already made for the project.
    if (!isPrivate && !allowedRoles?.length) {
      setAllowedRoles(agents.find((agent) => agent.id === agentId)?.allowed_roles ?? null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {project ? t("projects:editProjectTitle", { name: project.name }) : t("projects:editProject")}
          </DialogTitle>
          <DialogDescription>{t("projects:editProjectDescription")}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectName")}
            <Textarea
              autoFocus
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("projects:placeholders.namePlaceholder")}
              className="min-h-9 resize-none font-normal"
              rows={1}
            />
          </label>
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectAgent")}
            <AgentDropdown
              agents={agents}
              currentAgentId={defaultAgentId}
              loading={agentsLoading}
              error={agentsError}
              selectionLocked={false}
              getToken={getToken}
              onSelect={handleDefaultAgentChange}
              fullWidth
              isAdminUser={userRole === "admin"}
            />
            <span className="text-xs font-normal text-muted-foreground">
              {t("projects:projectAgentHint")}
            </span>
          </label>
          <label className="grid gap-1.5 text-sm font-medium">
            {t("projects:projectDirective")}
            <Textarea
              ref={directiveResizeRef}
              value={directive}
              onChange={(event) => {
                setDirective(event.target.value);
                resizeDirectiveForValueChange();
              }}
              placeholder={t("projects:placeholders.directivePlaceholder")}
              maxLength={500}
              className="min-h-9 resize-none overflow-hidden font-normal"
              rows={1}
            />
          </label>
          <ProjectShareSection
            initialSettings={{ isPrivate, allowedRoles }}
            userRole={userRole}
            resetKey={open ? `${project?.id ?? ""}:${defaultAgentId}` : ""}
            onSettingsChange={(settings: ProjectShareSettings) => {
              setIsPrivate(settings.isPrivate);
              setAllowedRoles(settings.allowedRoles);
            }}
          />
          <section className="grid gap-1.5 text-sm font-medium" aria-labelledby="project-wiki-links">
            <span id="project-wiki-links">{t("projects:wikiLinks")}</span>
            <div className="grid gap-2">
              {primaryWikiLinks.length > 0 && (
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t("projects:wikiLinksPrimary")}
                  </span>
                  <ul className="grid gap-1">
                    {primaryWikiLinks.map((link) => (
                      <li key={link.wiki_path}>
                        <span className="text-xs font-normal text-muted-foreground">{link.wiki_path}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="grid gap-1">
                <span className="text-xs font-medium text-muted-foreground">
                  {t("projects:wikiLinksReference")}
                </span>
                {referenceWikiLinks.length > 0 ? (
                  <ul className="grid gap-1">
                    {referenceWikiLinks.map((link) => (
                      <li key={link.wiki_path}>
                        <span className="text-xs font-normal text-muted-foreground">{link.wiki_path}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-xs font-normal text-muted-foreground">
                    {t("projects:wikiLinksEmpty")}
                  </span>
                )}
              </div>
            </div>
          </section>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {t("projects:updateProject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
